import React, { useEffect, useState } from 'react';

import axiosInstance from './axios';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

/**
 * DeactivateMFADialog - A dialog component for deactivating multifactor authentication.
 * 
 * This component provides a confirmation dialog to deactivate MFA for either the current user
 * or another user (when used by an admin). The dialog displays a confirmation message,
 * handles the deactivation request, and shows success or error feedback.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.dialogOpen - Controls whether the dialog is visible
 * @param {Function} props.setDialogOpen - Callback function to close the dialog (receives false)
 * @param {string|number} [props.userId] - Optional user ID for admin-initiated deactivation.
 *                                         If provided, uses `/users/${userId}/mfa/deactivate/` endpoint.
 *                                         If omitted, uses `auth/app/deactivate/` for self-deactivation.
 * @returns {JSX.Element} A Material-UI Dialog component for MFA deactivation
 */
export default function DeactivateMFADialog({ dialogOpen, setDialogOpen, userId }) {
	const [messageText, setMessageText] = useState(`Are you sure you want to deactivate multifactor authentication${userId ? " for this user" : ""}?`);
	const [showDeactivateButton, setShowDeactivateButton] = useState(true);

	useEffect(() => {
		setMessageText(`Are you sure you want to deactivate multifactor authentication${userId ? " for this user" : ""}?`);
		setShowDeactivateButton(true);
	}, [userId, dialogOpen]);

	const handleClose = () => {
		setDialogOpen(false);
	};

	const handleDeactivate = () => {
		const endpoint = userId 
			? `/users/${userId}/mfa/deactivate/`
			: 'auth/app/deactivate/';
		
		axiosInstance.post(endpoint, {})
			.then((res) => {
				if (res.status === 204 || res.status === 200) {
					setMessageText('Multifactor authentication has been deactivated.');
					setShowDeactivateButton(false);
				} else {
					setMessageText('An error occurred while deactivating multifactor authentication.');
					setShowDeactivateButton(false);
					console.log('Invalid response from server:', res);
				}
			})
			.catch((error) => {
				setMessageText('An error occurred while deactivating multifactor authentication.');
				setShowDeactivateButton(false);
				console.log(`MFA deactivation failed: ${error}`);
			});
	};

	return (
		<Dialog
			open={dialogOpen}
			onClose={handleClose}
			aria-labelledby='deactivate-mfa-dialog-title'
			maxWidth='xs'
		>
			<DialogTitle id='deactivate-mfa-dialog-title'>
				<Typography variant="h6" component="div" fontFamily='Saira, sans-serif' fontWeight={600}>
					Deactivate Multifactor Authentication
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Typography fontSize='12' fontFamily='Saira, sans-serif' sx={{ marginLeft: 2 }}>
					{messageText}
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={handleClose}
					color='primary'
					variant={ showDeactivateButton ? 'outlined' : 'contained' }
				>
					{ showDeactivateButton ? 'Cancel' : 'Close' }
				</Button>
				{ showDeactivateButton && (
					<Button onClick={handleDeactivate} color='primary' variant='contained'>
						Deactivate
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}